import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type MotionProps } from 'framer-motion';
import { ArrowDown, MapPin, Sparkle } from 'lucide-react';
import { heroChips, profile } from '@/lib/portfolio';
import { SocialLinks } from './SocialLinks';
import { Starfield } from './Starfield';

function RotatingRole() {
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % profile.roles.length),
      2200,
    );
    return () => window.clearInterval(id);
  }, []);

  if (reduce) {
    return <span className="text-nova">{profile.roles[0]}</span>;
  }

  return (
    <span className="relative inline-flex h-[1.4em] min-w-[16.5rem] items-center overflow-hidden align-middle md:min-w-[21.5rem]">
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ y: '110%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '-110%', opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.21, 0.65, 0.15, 1] }}
          className="absolute whitespace-nowrap text-nova"
        >
          {profile.roles[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function OrbitalBadge() {
  return (
    <div className="pointer-events-none absolute right-10 top-28 hidden size-44 lg:block xl:right-24" aria-hidden="true">
      <svg viewBox="0 0 200 200" className="size-full animate-spin-slower">
        <defs>
          <path
            id="badge-orbit"
            d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0"
            fill="none"
          />
        </defs>
        <text className="fill-foreground/60 font-display text-[11.5px] uppercase" style={{ letterSpacing: '0.32em' }}>
          <textPath href="#badge-orbit">
            immaculate munde · builder · cto · nairobi ·
          </textPath>
        </text>
      </svg>
      <span className="absolute inset-0 flex items-center justify-center">
        <Sparkle className="size-7 text-flare" />
      </span>
    </div>
  );
}

export function Hero() {
  const reduce = useReducedMotion();

  const rise = (delay: number): MotionProps =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 32 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, delay, ease: [0.21, 0.65, 0.15, 1] },
        };

  return (
    <section id="top" className="relative flex min-h-screen flex-col overflow-hidden">
      {/* Atmosphere */}
      <div className="bg-grid absolute inset-0" aria-hidden="true" />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,hsl(258_34%_5%)_78%)]"
        aria-hidden="true"
      />
      <div className="absolute -left-40 -top-40 size-[36rem] rounded-full bg-pulsar/25 blur-[130px]" aria-hidden="true" />
      <div className="absolute -right-44 top-1/4 size-[32rem] rounded-full bg-nova/20 blur-[130px]" aria-hidden="true" />
      <div className="absolute bottom-0 left-1/3 size-[28rem] rounded-full bg-comet/15 blur-[130px]" aria-hidden="true" />
      <Starfield />
      <OrbitalBadge />

      {/* Content */}
      <div className="container relative z-10 flex flex-1 flex-col justify-center pb-28 pt-36">
        <motion.div {...rise(0)} className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            <MapPin className="size-3.5 text-nova" />
            Nairobi, Kenya
          </span>
          <span className="flex items-center gap-2 rounded-full border border-mint/25 bg-mint/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-mint">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-mint opacity-60" />
              <span className="relative inline-flex size-2 rounded-full bg-mint" />
            </span>
            Open to wild ideas
          </span>
        </motion.div>

        <motion.h1
          {...rise(0.1)}
          className="mt-8 font-display font-extrabold uppercase leading-[0.95] tracking-tight"
        >
          <span className="block text-[clamp(2rem,9vw,7rem)]">{profile.firstName}</span>
          <span className="block bg-gradient-to-r from-nova via-blush to-pulsar bg-clip-text text-[clamp(2rem,9vw,7rem)] text-transparent">
            {profile.lastName}
          </span>
        </motion.h1>

        <motion.p {...rise(0.2)} className="mt-6 font-display text-lg font-medium md:text-2xl">
          <span className="mr-2 inline-block text-flare" aria-hidden="true">✦</span>
          <RotatingRole />
        </motion.p>

        <motion.p {...rise(0.3)} className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
          {profile.tagline}
        </motion.p>

        <motion.div {...rise(0.4)} className="mt-8 flex flex-wrap gap-2.5">
          {heroChips.map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-white/10 bg-card/60 px-4 py-1.5 text-sm text-muted-foreground backdrop-blur transition-colors hover:border-white/25 hover:text-foreground"
            >
              {chip}
            </span>
          ))}
        </motion.div>

        <motion.div {...rise(0.5)} className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#work"
            className="group flex items-center gap-2 rounded-full bg-nova px-7 py-3.5 font-semibold text-primary-foreground shadow-[0_0_40px_-8px_hsl(14_95%_60%/0.7)] transition-all hover:scale-[1.03] hover:shadow-[0_0_60px_-8px_hsl(14_95%_60%/0.9)]"
          >
            Enter the verse
            <ArrowDown className="size-4 transition-transform group-hover:translate-y-0.5" />
          </a>
          <a
            href="#journey"
            className="rounded-full border border-white/15 px-7 py-3.5 font-semibold text-foreground transition-colors hover:border-white/40 hover:bg-white/5"
          >
            The story so far
          </a>
          <span className="hidden h-6 w-px bg-white/15 sm:block" aria-hidden="true" />
          <SocialLinks />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.a
        href="#about"
        aria-label="Scroll to story"
        {...(reduce
          ? {}
          : {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              transition: { delay: 1.2, duration: 1 },
            })}
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <span className="text-[0.65rem] font-medium uppercase tracking-[0.35em]">Scroll</span>
        <ArrowDown className="size-4 animate-bounce" />
      </motion.a>
    </section>
  );
}
